import { useState } from 'react';
import DonutChart from '../DonutChart';
import { Typography } from '../../../widgets/Typography';
import { ChevronBottomIcon } from '../../../../assets/icons/ChevronBottomIcon';
import {
  PieChartClusterMeta,
  WidgetDefinition,
} from '../../../../shared/types/widget';

interface DimensionQuestionBreakdownProps {
  definition: WidgetDefinition;
  content: PieChartClusterMeta;
}

export default function DimensionQuestionBreakdown({
  definition,
  content,
}: DimensionQuestionBreakdownProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  // unique responses count can be derived from the dataset data by summing up the values
  const getTotalUniqueResponses = (datasetData: string[]) => {
    return datasetData.reduce(
      (acc: number, curr: string) => acc + Number(curr),
      0,
    );
  };

  return (
    <div className="mx-6 mb-6 flex flex-col gap-y-4">
      {content?.data?.map((question, index) => (
        <div className="bg-taupe-50 border-taupe-550 rounded-lg border p-4" key={question.title}>
          {/* Question Header */}
          <div className="mb-4">
            <Typography variant="body-lg" weight="bold" className="mb-2">
              Question {index + 1}
            </Typography>
            {/* Question Text with border */}
            <div className="mb-4 border-l-2 border-blue-700 py-2 pl-4">
              <Typography variant="body-md" weight="medium" className="italic">
                {question.title}
              </Typography>
            </div>
            <Typography variant="body-md" weight="medium">
              Total unique responses:
              {getTotalUniqueResponses(question.datasets[0].data)}
            </Typography>
          </div>

          {/* Breakdown Answer Toggle */}
          <div className="flex items-center justify-end">
            {/* Divider Line */}
            <div className="bg-taupe-550 mr-4 h-px flex-1" />

            <button
              onClick={toggleExpanded}
              className="flex cursor-pointer items-center gap-2 transition-all duration-200"
            >
              <Typography
                variant="body-md"
                weight="medium"
                className="!text-blue-700"
              >
                Breakdown Answer
              </Typography>
              <div
                className={`transition-transform duration-300 ease-in-out ${
                  isExpanded ? 'rotate-180' : 'rotate-0'
                }`}
              >
                <ChevronBottomIcon className="stroke-blue-700" />
              </div>
            </button>
          </div>

          {/* Collapsible Content */}
          <div
            className={`overflow-hidden transition-all duration-500 ease-in-out ${
              isExpanded ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
            }`}
          >
            <div className="flex w-full flex-col items-center justify-center pt-6 pb-2">
              <DonutChart
                data={{
                  labels: question.labels,
                  values: question.datasets[0].data.map((data) => Number(data)),
                  percentages: question.datasets[0].data.map((data, index) => {
                    return Math.ceil(
                      (Number(data) /
                        question.datasets[0].data.reduce(
                          (acc, curr) => acc + Number(curr),
                          0,
                        )) *
                        100,
                    );
                  }),
                  backgroundColor: content.meta.backgroundColors || [],
                }}
                className="w-full"
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
